// zipFiles.ts
// Zips an array of files into a single Blob, preserving folder structure via fullPath.
// Uses fflate for fast client-side compression (~29KB gzipped, no dependencies).
// Used by SkillCreateForm to zip a dropped folder before uploading to S3.

import { Zippable, zipSync } from 'fflate';

interface FileWithPath extends File {
  fullPath?: string;
}

/**
 * Reads a File into a Uint8Array for use with fflate.
 */
const readFileAsUint8Array = (file: File): Promise<Uint8Array> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Zips files into a single Blob, preserving folder paths.
 * Uses each file's fullPath (from traverseFileTree) as the zip entry path.
 * Falls back to file.name if fullPath is not set.
 */
export const zipFiles = async (files: FileWithPath[]): Promise<Blob> => {
  const data: Zippable = {};

  // Read all files in parallel for performance
  const entries = await Promise.all(
    files.map(async (file) => ({
      path: file.fullPath || file.name,
      content: await readFileAsUint8Array(file),
    })),
  );

  // Build the fflate input object using paths as keys.
  // fflate handles nested paths (e.g., "src/lib/b.txt") when they contain "/" separators.
  entries.forEach((entry) => {
    data[entry.path] = entry.content;
  });

  const zipped = zipSync(data);
  return new Blob([zipped], { type: 'application/zip' });
};
